import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { openPartnerLink } from '../../utils/openPartnerLink';
import { getPromoActionLabel } from '../../utils/promoUi';
import { useVipBadges } from '../../hooks/useVipBadges';
import VipBadge from '../../components/base/VipBadge';
import { buildCategoryCollection, buildSubcategoryId, getCategoryMeta } from '../../utils/categoryCatalog';
import { readPersistedStores } from '../../utils/storeSeed';
import { buildCategorySeo, usePageSeo } from '../../utils/seo';

interface StorePromo {
  id: string;
  code: string;
  discount: string;
  description: string;
  expiryDate: string;
  isActive: boolean;
  isTopDeal?: boolean;
  image?: string;
  partnerUrl?: string;
}

interface Store {
  id: string;
  name: string;
  logo: string;
  description: string;
  category: string;
  subcategory?: string;
  rating: number;
  isActive: boolean;
  createdAt: string;
  promos: StorePromo[];
}

interface CategoryData {
  id: string;
  name: string;
  description: string;
  icon: string;
  bgGradient: string;
  stores: Store[];
  promos: Array<StorePromo & { storeName: string }>;
  subcategories: Array<{ id: string; name: string; description: string }>;
}

const getStoreSlug = (storeName: string): string => {
  return storeName.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9а-яё\-]/gi, '')
    .replace(/\.ru$/, '')
    .replace(/\.com$/, '');
};

export default function CategoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(
    searchParams.get('subcategory')
  );
  const [revealedCodes, setRevealedCodes] = useState<Set<string>>(new Set());
  const [category, setCategory] = useState<CategoryData | null>(null);
  const { getVipBadge, isHighlighted } = useVipBadges();

  const categorySeo = category
    ? buildCategorySeo(category.name, category.stores.length, category.promos.length)
    : {
        title: 'Категория не найдена | Promokod.online',
        description: 'Запрошенная категория не найдена или недоступна в публичной версии Promokod.online.',
      };

  usePageSeo({
    title: categorySeo.title,
    description: categorySeo.description,
    canonicalPath: id ? `/category/${id}` : '/categories',
    noIndex: !category,
  });

  useEffect(() => {
    if (!id) return;

    const stores = readPersistedStores(localStorage) as Store[];
    const categoryGroup = buildCategoryCollection(stores).find((entry) => entry.id === id);

    if (!categoryGroup) {
      setCategory(null);
      return;
    }

    // Собираем все промокоды из магазинов категории
    const categoryPromos: Array<StorePromo & { storeName: string }> = [];
    categoryGroup.stores.forEach(store => {
      store.promos.forEach(promo => {
        if (promo.isActive) {
          categoryPromos.push({
            ...promo,
            storeName: store.name
          });
        }
      });
    });

    // Собираем уникальные подкатегории
    const subcategories = categoryGroup.subcategories.map(sub => ({
      id: buildSubcategoryId(sub),
      name: sub,
      description: `Промокоды и скидки на ${sub.toLowerCase()}`
    }));

    const meta = getCategoryMeta(categoryGroup.name);

    setCategory({
      id,
      name: categoryGroup.name,
      description: meta.description,
      icon: meta.icon,
      bgGradient: meta.bgGradient,
      stores: categoryGroup.stores,
      promos: categoryPromos,
      subcategories
    });
  }, [id]);

  useEffect(() => {
    const subcategoryParam = searchParams.get('subcategory');
    if (subcategoryParam) {
      setSelectedSubcategory(subcategoryParam);
    }
  }, [searchParams]);

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Категория не найдена</h1>
          <button 
            onClick={() => navigate('/categories')}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Вернуться к категориям
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // Фильтрация магазинов по подкатегории
  const filteredStores = selectedSubcategory
    ? category.stores.filter(store => buildSubcategoryId(store.subcategory ?? '') === selectedSubcategory)
    : category.stores;

  // Фильтрация промокодов по подкатегории
  const filteredPromos = selectedSubcategory
    ? category.promos.filter(promo => {
        const store = category.stores.find(s => s.name === promo.storeName);
        return buildSubcategoryId(store?.subcategory ?? '') === selectedSubcategory;
      })
    : category.promos;

  // Поиск
  const searchFilteredStores = searchQuery
    ? filteredStores.filter(store =>
        store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        store.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredStores;

  const searchFilteredPromos = searchQuery
    ? filteredPromos.filter(promo =>
        promo.storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        promo.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        promo.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredPromos;

  const handleSubcategoryClick = (subcategoryId: string | null) => {
    setSelectedSubcategory(subcategoryId);
    if (subcategoryId) {
      setSearchParams({ subcategory: subcategoryId });
    } else {
      setSearchParams({});
    }
  };

  const handleRevealCode = (promoId: string, code: string, partnerUrl?: string) => {
    // Показываем промокод
    setRevealedCodes(prev => new Set([...prev, promoId]));
    
    // Копируем код в буфер обмена
    if (code) {
      navigator.clipboard.writeText(code);
    }

    openPartnerLink(partnerUrl);
  };

  const isCodeRevealed = (promoId: string) => revealedCodes.has(promoId);

  const currentSubcategory = selectedSubcategory
    ? category.subcategories?.find(sub => sub.id === selectedSubcategory)
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className={`bg-gradient-to-br ${category.bgGradient} text-white py-16`}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/categories')}
              className="text-white/80 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            <nav className="text-sm text-white/80">
              <span className="hover:text-white cursor-pointer" onClick={() => navigate('/categories')}>
                Категории
              </span>
              <span className="mx-2">/</span>
              <span className="text-white">{category.name}</span>
              {currentSubcategory && (
                <>
                  <span className="mx-2">/</span>
                  <span className="text-white">{currentSubcategory.name}</span>
                </>
              )}
            </nav>
          </div>
          
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
              <i className={`${category.icon} text-4xl text-white`}></i>
            </div>
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {currentSubcategory ? currentSubcategory.name : category.name}
              </h1>
              <p className="text-xl mb-6 opacity-90 max-w-3xl">
                {currentSubcategory ? currentSubcategory.description : category.description}
              </p>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-store-line text-lg"></i>
                  <span>{filteredStores.length} магазинов</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-price-tag-line text-lg"></i>
                  <span>{filteredPromos.length} промокодов</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-refresh-line text-lg"></i>
                  <span>Обновлено сегодня</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Подкатегории */}
      {category.subcategories && category.subcategories.length > 0 && (
        <section className="bg-white border-b border-gray-200 sticky top-16 z-10 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => handleSubcategoryClick(null)}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-all cursor-pointer ${
                  !selectedSubcategory
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-apps-line mr-2"></i>
                Все
              </button>
              {category.subcategories.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => handleSubcategoryClick(sub.id)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap transition-all cursor-pointer ${
                    selectedSubcategory === sub.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {sub.name}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Search Bar */}
      <section className="py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="relative max-w-2xl mx-auto">
            <input
              type="text"
              placeholder="Поиск магазинов и промокодов..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 pr-12 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
            />
            <i className="ri-search-line absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
          </div>
        </div>
      </section>

      {/* Promocodes Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            <i className="ri-price-tag-3-line text-orange-500 mr-3"></i>
            Актуальные промокоды
          </h2>
          
          {searchFilteredPromos.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl">
              <i className="ri-coupon-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">
                {searchQuery ? 'Промокоды не найдены' : 'В этой подкатегории пока нет промокодов'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {searchFilteredPromos.map(promo => {
                const promoStore = category.stores.find(s => s.name === promo.storeName);
                const vip = promoStore ? getVipBadge(promoStore.id) : null;
                const highlighted = promoStore ? isHighlighted(promoStore.id) : false;
                return (
                <div key={promo.id} className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border ${
                  highlighted ? 'border-amber-300 ring-1 ring-amber-200 bg-amber-50/30' : 'border-gray-100'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xl font-bold text-gray-700">
                            {promo.storeName?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900">{promo.storeName}</h3>
                            {vip && <VipBadge badge={vip.badge} badgeColor={vip.badgeColor} />}
                          </div>
                          <span className="text-orange-600 font-bold text-lg">{promo.discount}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{promo.description}</p>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-50 border-2 border-dashed border-orange-300 rounded-lg px-4 py-3">
                      <span className={`font-mono font-bold text-orange-600 ${!isCodeRevealed(promo.id) ? 'filter blur-sm select-none' : ''}`}>
                        {promo.code}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRevealCode(promo.id, promo.code, promo.partnerUrl)}
                      className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors font-medium whitespace-nowrap cursor-pointer"
                    >
                      {isCodeRevealed(promo.id) ? (
                        <>
                          <i className="ri-check-line mr-2"></i>
                          Получено
                        </>
                      ) : (
                        <>
                          <i className="ri-eye-line mr-2"></i>
                          {getPromoActionLabel(promo.code)}
                        </>
                      )}
                    </button>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <span>
                      <i className="ri-time-line mr-1"></i>
                      До {promo.expiryDate}
                    </span>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Stores Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            <i className="ri-store-2-line text-orange-500 mr-3"></i>
            Магазины в категории
          </h2>
          
          {searchFilteredStores.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-store-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">
                {searchQuery ? 'Магазины не найдены' : 'В этой подкатегории пока нет магазинов'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {searchFilteredStores.map(store => {
                const vip = getVipBadge(store.id);
                const highlighted = isHighlighted(store.id);
                return (
                <div 
                  key={store.id}
                  onClick={() => navigate(`/store/${getStoreSlug(store.name)}`)}
                  className={`bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group border ${
                    highlighted ? 'border-amber-300 ring-1 ring-amber-200 bg-amber-50/50' : 'border-gray-100'
                  }`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow">
                      {store.logo ? (
                        <img src={store.logo} alt={store.name} className="w-12 h-12 object-contain" />
                      ) : (
                        <span className="text-2xl font-bold text-gray-700">
                          {store.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
                          {store.name}
                        </h3>
                        {vip && <VipBadge badge={vip.badge} badgeColor={vip.badgeColor} />}
                      </div>
                      {store.rating > 0 && (
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`ri-star-${i < Math.floor(store.rating) ? 'fill' : 'line'} text-yellow-400 text-sm`}
                            ></i>
                          ))}
                          <span className="text-sm text-gray-500 ml-1">
                            {store.rating}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {store.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-orange-600 font-medium">
                      <i className="ri-price-tag-line mr-1"></i>
                      {store.promos.filter(p => p.isActive).length} промокодов
                    </span>
                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-orange-500 group-hover:translate-x-1 transition-all"></i>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
